import { createSvgElement } from '@ardoq/dom-utils';
import { DEFAULT_ICON_COLOR, NEGATIVE_COLOR_CSS_VARIABLE } from './const';
import { IconInfoForSvgImage, IconSize } from './types';

/**
 * Cache for svg elements, set to null to skip caching.
 * Null is used instead of undefined to force developer to communicate
 * intention and prevent us from forgetting it.
 */
type SvgElementCache = Map<string, SVGSVGElement> | null;

const cloneSvgNode = (iconHRef: string, svgElementCache: SvgElementCache) => {
  if (svgElementCache?.has(iconHRef)) {
    return svgElementCache.get(iconHRef);
  }

  // get icon element from the document
  const iconElement = document.querySelector(iconHRef)?.cloneNode(true) as
    | Element
    | undefined;
  if (!iconElement) {
    return null;
  }

  // each <use> has to be replaced by a copy the definition it references
  iconElement.querySelectorAll('use').forEach(useElement => {
    const useHref = useElement.href.baseVal;

    // resolve and clone the <use> elements in the icon
    const resolved = cloneSvgNode(useHref, svgElementCache)?.cloneNode(true) as
      | Element
      | undefined;
    if (!resolved) {
      return;
    }

    // copy attributes from the <use> element to the resolved element
    ['width', 'height', 'fill', 'stroke', 'color'].forEach(attribute => {
      const value = useElement.getAttribute(attribute);
      if (value) {
        resolved.setAttribute(attribute, value);
      }
    });

    const transform = useElement.getAttribute('transform');
    if (transform) {
      // svg elements do not support the transform attribute.
      const container = createSvgElement('g', { transform });
      container.appendChild(resolved);
      useElement.replaceWith(container);
    } else {
      useElement.replaceWith(resolved);
    }
  });

  return iconElement?.tagName === 'symbol'
    ? wrapSymbolInSvg(iconElement)
    : (iconElement as SVGSVGElement);
};

const wrapSymbolInSvg = (symbol: Element) => {
  const svg = createSvgElement('svg', {
    viewBox: symbol.getAttribute('viewBox') ?? '',
  });
  // Carry over presentation attributes from the symbol so that child elements
  // inheriting from it (e.g. paths with implicit `fill="currentColor"`) keep
  // rendering correctly once they live under the new <svg>.
  ['fill', 'stroke', 'color'].forEach(attribute => {
    const value = symbol.getAttribute(attribute);
    if (value) {
      svg.setAttribute(attribute, value);
    }
  });
  symbol.childNodes.forEach(node => svg.appendChild(node.cloneNode()));
  return svg;
};

export const createSvgImageUrl = (
  iconInfo: IconInfoForSvgImage,
  svgElementCache: SvgElementCache
) => {
  const [
    iconId,
    color = DEFAULT_ICON_COLOR,
    fill = '',
    stroke = '',
    width = '100%',
    height = '',
    negativeColor = '',
  ] = iconInfo.split('~~');

  const cssVarMatch = color.match(/var\((--[\w\d-]+)\)/);
  const resolvedColor = cssVarMatch
    ? getComputedStyle(document.body).getPropertyValue(cssVarMatch[1])
    : color;

  const svg = cloneSvgNode(`#${iconId}`, svgElementCache)?.cloneNode(
    true
  ) as SVGSVGElement | null;

  if (!svg) {
    return '';
  }

  if (isFinite(Number(width)) && isFinite(Number(height))) {
    svg.setAttribute('width', `${width}px`);
    svg.setAttribute('height', `${height}px`);
  } else {
    // Firefox needs the width and height attributes set or else it will not draw on canvas
    // `${IconSize.MEDIUM}px` is an arbitrary default if no finite dimensions are specified
    svg.setAttribute('height', `${IconSize.MEDIUM}px`);
    svg.setAttribute('width', `${IconSize.MEDIUM}px`);
  }

  svg.setAttribute('color', resolvedColor);
  svg.style.width = width;
  if (height) {
    svg.style.height = height;
  } else {
    svg.style.aspectRatio = '1 / 1';
  }

  if (fill && !svg.getAttribute('fill')) {
    svg.setAttribute('fill', fill);
  }
  if (stroke && !svg.getAttribute('stroke')) {
    svg.setAttribute('stroke', stroke);
  }
  if (negativeColor) {
    svg.style.setProperty(NEGATIVE_COLOR_CSS_VARIABLE, negativeColor);
  }

  const markup = new window.XMLSerializer().serializeToString(svg);
  return `data:image/svg+xml;base64,${window.btoa(markup)}`;
};
